import { ApolloQueryResult, gql } from '@apollo/client';
import { Dictionary, InputChangeHandlerArgs, OPEN_GRAPH_QUERY, OpenGraphResult } from '@shapeable/core';
import produce from 'immer';
import { each, get } from 'lodash';

const BILIBILI_URL_RE = /(^|\/\/|\.)bilibili\.com|(^|\/\/|\.)b23\.tv/i;

const isValidHttpUrl = (value = '') => {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const replaceFieldValue = (draft: any, key: string, fieldState: Dictionary<any>, value: any, valueKey = 'value', force = false) => {
  draft[key] = draft[key] || {};
  if (value && (force || !get(fieldState, `${key}.${valueKey}`))) {
    draft[key][valueKey] = value;
    draft[key].loadingMessage = '';
    draft[key].disabled = false;
  }
};

const restoreVideoFieldState = ({ fieldState, onChange }: any) => {
  const newState = produce(fieldState, draft => {
    each(['name', 'thumbnail', 'description'], key => {
      draft[key] = draft[key] || {};
      draft[key].loadingMessage = '';
      draft[key].disabled = false;
    });
  });

  onChange(newState);
};

const handleBilibiliUrlBlur = async ({ value, fieldState, onChange, client }: InputChangeHandlerArgs ) => {
  const { thumbnail = { value: '' }, image = { value: '' } } = fieldState || {};
  const hasImage = thumbnail.value || image.value;

  if (hasImage || !isValidHttpUrl(value || '') || !BILIBILI_URL_RE.test(value || '')) {
    return false;
  }

  const loadingFieldState = produce(fieldState, draft => {
    each(['name', 'thumbnail', 'description'], key => {
      draft[key] = draft[key] || {};
      draft[key].loadingMessage = 'Fetching';
      draft[key].disabled = true;
    });
  });

  onChange(loadingFieldState);

  try {
    const { data }: ApolloQueryResult<{ openGraph: OpenGraphResult }> = await client.query({
      query: OPEN_GRAPH_QUERY,
      variables: { url: value },
    });

    const openGraph = get(data, 'openGraph');
    if (!openGraph) {
      restoreVideoFieldState({ fieldState, onChange });
      return false;
    }

    const newFieldState = produce(fieldState, draft => {
      replaceFieldValue(draft, 'name', fieldState, openGraph.title, 'value', false);
      replaceFieldValue(draft, 'description', fieldState, openGraph.description, 'value', false);
      replaceFieldValue(draft, 'thumbnail', fieldState, openGraph.image, 'value', false);
    });

    onChange(newFieldState);
    return true;
  } catch (err) {
    restoreVideoFieldState({ fieldState, onChange });
    return false;
  }
};

export const installBilibiliVideoUrlBlur = (videoConfig: any) => {
  if (!videoConfig?.form?.fields?.url) {
    return;
  }

  // Store base onBlur handler to call if url doesn't match bilibili pattern or after handling bilibili urls
  const baseUrlOnBlur = get(videoConfig, 'form.fields.url.onBlur');

  videoConfig.form.fields.url.onBlur = async (args: any) => {
    const url = get(args, 'value', '');
    if (BILIBILI_URL_RE.test(url || '')) {
      const handled = await handleBilibiliUrlBlur(args);
      if (handled) {
        return;
      }
    }

    if (baseUrlOnBlur) {
      await baseUrlOnBlur(args);
    }
  };
};
